// ── BCHNostr — on-chain tip verification (faucet anti-fraud) ──────
//
// The faucet counts a user's "tips" from self-published kind 9735 receipts
// (t=bch-tip). Those receipts are just signed JSON — anyone can mint one with
// an arbitrary `amount` and recipient WITHOUT sending any BCH, so on their own
// they are not proof of payment.
//
// Genuine tips DO record the on-chain `txid` in their content (see
// BCHTipButton). This module cross-checks those txids against the BCH chain.
//
// ── Important: this is an ADVISORY aid, not a fraud verdict ──
// Real tips are frequently SPLIT between a recipient's PERSONAL address and
// their COMMUNITY-wallet address (and always carry a separate platform-fee
// output). A tip is therefore treated as verified when its txid pays EITHER of
// the recipient's known addresses. Anything we can't positively confirm —
// because the recipient never published an address, the split went somewhere
// we can't resolve, or an explorer was unreachable — is reported as
// 'unverified'/'unknown', NEVER as fraud. Only a receipt that carries no txid
// at all is a hard negative, and even then this screen only INFORMS the
// operator; it never blocks a real person automatically.

import type { Event } from 'nostr-tools';
import { verifyTxPaysAddress } from './paymentAudit';
import { prefetchBCHAddress } from '../hooks/useBCHAddress';
import { prefetchCommunityWalletInfo } from '../hooks/useCommunityWallet';

const TXID_RE = /^[0-9a-f]{64}$/;

export type TipVerdict =
  /** txid is a real tx that pays one of the recipient's known addresses. */
  | 'verified'
  /** receipt carries no usable txid — cannot be a real on-chain tip. */
  | 'no-txid'
  /** tx exists but we couldn't match it to a known recipient address, OR the
   *  recipient has no resolvable address. NOT fraud — often a split tip whose
   *  outputs we can't fully resolve. */
  | 'unverified'
  /** explorers unreachable this run — retry later (not evidence of fraud). */
  | 'unknown';

export interface TipCheck {
  eventId: string;
  txid: string;
  recipient: string;
  verdict: TipVerdict;
  sats: number;
}

export interface TipVerificationSummary {
  /** How many bch-tip receipts we actually found for this claimant. */
  receiptsFound: number;
  /** How many receipts we sampled for on-chain checking. */
  total: number;
  verified: number;
  noTxid: number;
  unverified: number;
  unknown: number;
  checks: TipCheck[];
}

/** Reduce a kind-9735 bch-tip receipt to { txid, recipient }. */
function parseTipReceipt(ev: Event, claimant: string): { txid: string; recipient: string } | null {
  const p = ev.tags.find(t => t[0] === 'p')?.[1] || '';
  // A qualifying tip is to someone OTHER than the claimant.
  if (!p || p === claimant) return null;
  let txid = '';
  try {
    const c = JSON.parse(ev.content) as { txid?: unknown };
    if (typeof c.txid === 'string') txid = c.txid.trim().toLowerCase();
  } catch { /* content may be empty */ }
  // Some receipts stash the txid in a `fee` tag instead.
  if (!TXID_RE.test(txid)) {
    const feeTag = ev.tags.find(t => t[0] === 'fee')?.[1] || '';
    if (TXID_RE.test(feeTag.toLowerCase())) txid = feeTag.toLowerCase();
  }
  return { txid, recipient: p };
}

/** Resolve every BCH address a tip to `recipient` could legitimately pay:
 *  their personal address AND their community-wallet address (split tips). */
async function recipientAddresses(recipient: string): Promise<string[]> {
  const addrs = new Set<string>();
  const [personal, community] = await Promise.all([
    prefetchBCHAddress(recipient).catch(() => null),
    prefetchCommunityWalletInfo(recipient).then(i => i.address).catch(() => null),
  ]);
  if (personal) addrs.add(personal);
  if (community) addrs.add(community);
  return [...addrs];
}

/**
 * Verify a claimant's tip receipts on-chain.
 *
 * `tips` are the claimant's kind-9735 bch-tip events. To keep explorer load
 * bounded, only the newest `maxChecks` distinct-recipient tips are audited
 * (that's all the faucet's "3+ unique users" rule ever depends on).
 */
export async function verifyClaimantTips(
  claimant: string,
  tips: Event[],
  maxChecks = 12,
): Promise<TipVerificationSummary> {
  // Newest first, de-duplicated by recipient so we sample real breadth.
  const seenRecipient = new Set<string>();
  const candidates: { eventId: string; txid: string; recipient: string }[] = [];
  let receiptsFound = 0;

  for (const ev of [...tips].sort((a, b) => b.created_at - a.created_at)) {
    const parsed = parseTipReceipt(ev, claimant);
    if (!parsed) continue;
    receiptsFound++;
    if (seenRecipient.has(parsed.recipient)) continue;
    seenRecipient.add(parsed.recipient);
    if (candidates.length < maxChecks) candidates.push({ eventId: ev.id, ...parsed });
  }

  const checks: TipCheck[] = [];
  for (const c of candidates) {
    if (!TXID_RE.test(c.txid)) {
      checks.push({ ...c, verdict: 'no-txid', sats: 0 });
      continue;
    }
    const addrs = await recipientAddresses(c.recipient);
    if (addrs.length === 0) {
      // Can't resolve any address for the recipient — inconclusive, not fraud.
      checks.push({ ...c, verdict: 'unverified', sats: 0 });
      continue;
    }
    // Check the txid against each candidate address; a hit on ANY confirms it.
    let sats = 0;
    let paid = false;
    let reachable = false;
    for (const addr of addrs) {
      const res = await verifyTxPaysAddress(c.txid, addr);
      if (res === null) continue; // explorer failure for this address
      reachable = true;
      if (res.paid) { paid = true; sats = Math.max(sats, res.sats); break; }
    }
    if (paid) checks.push({ ...c, verdict: 'verified', sats });
    else if (!reachable) checks.push({ ...c, verdict: 'unknown', sats: 0 });
    else checks.push({ ...c, verdict: 'unverified', sats: 0 });
  }

  return {
    receiptsFound,
    total: checks.length,
    verified: checks.filter(c => c.verdict === 'verified').length,
    noTxid: checks.filter(c => c.verdict === 'no-txid').length,
    unverified: checks.filter(c => c.verdict === 'unverified').length,
    unknown: checks.filter(c => c.verdict === 'unknown').length,
    checks,
  };
}

/** Human summary of a verification result for the operator UI. */
export function describeTipVerification(s: TipVerificationSummary): string {
  if (s.receiptsFound === 0) return 'No tip receipts found for this account';
  if (s.total === 0) return `${s.receiptsFound} receipts found`;
  const parts: string[] = [`${s.verified}/${s.total} confirmed on-chain`];
  if (s.noTxid) parts.push(`${s.noTxid} without a txid`);
  if (s.unverified) parts.push(`${s.unverified} unconfirmed`);
  if (s.unknown) parts.push(`${s.unknown} unchecked (explorer busy)`);
  return `${s.receiptsFound} receipts · ` + parts.join(' · ');
}

/**
 * Strong evidence a claim is fabricated — deliberately conservative so real
 * people are never mislabelled. True ONLY when receipts exist, at least one
 * carries NO txid, and NONE could be confirmed on-chain. "Unverified" and
 * "unknown" (address-resolution / explorer issues) never count as fraud, and
 * a claimant with zero receipts is inconclusive, not fraudulent.
 */
export function looksFabricated(s: TipVerificationSummary): boolean {
  return s.total > 0 && s.verified === 0 && s.noTxid > 0 && s.unverified === 0 && s.unknown === 0;
}
